import { Link } from "react-router-dom";
import { MapPin, Phone, Mail } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();

  return (
    <footer data-testid="footer" className="bg-[#0F172A] text-white">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            {/* Logo - aligned with Navigation/Nos Services/Contact titles line */}
            <div className="h-[28px] flex items-center mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_affiche-display/artifacts/ctzkzd3a_Nettis%20blanc%20transparent.png"
                alt="Nettis Facility"
                className="h-[127px] w-auto"
              />
            </div>
            {/* Paragraph - aligned with Accueil line */}
            <p className="text-slate-400 text-sm leading-relaxed">
              {t('footerDescription')}
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-lg font-semibold mb-6">{t('navigation')}</h3>
            <ul className="space-y-3">
              {[
                { href: "/", label: t('accueil') },
                { href: "/services", label: t('nosServicesMenu') },
                { href: "/zones-intervention", label: "Zones d'intervention" },
                { href: "/engagement-rse", label: t('engagementRse') },
                { href: "/a-propos", label: t('aPropos') },
                { href: "/postuler", label: t('postuler') },
                { href: "/contact", label: t('contact') },
                { href: "/faq", label: "FAQ" },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-slate-400 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-6">{t('nosServices')}</h3>
            <ul className="space-y-3">
              {[
                { href: "/services/nettoyage-bureaux", label: t('nettoyageBureaux') },
                { href: "/services/nettoyage-industriel", label: t('nettoyageIndustriel') },
                { href: "/services/entretien-immeubles", label: t('entretienImmeubles') },
                { href: "/services/nettoyage-apres-travaux", label: t('nettoyageApresTravaux') },
                { href: "/services/nettoyage-vitres", label: t('vitresSurfaces') },
                { href: "/services/desinfection", label: t('desinfection') },
              ].map((service) => (
                <li key={service.href}>
                  <Link
                    to={service.href}
                    className="text-slate-400 hover:text-white transition-colors text-sm"
                  >
                    {service.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-semibold mb-6">{t('contact')}</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#10B981] mt-0.5 shrink-0" />
                <span className="text-slate-400 text-sm">
                  Avenue Louise 65 boîte 11<br />
                  1050 Bruxelles, {t('belgique')}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-[#10B981] shrink-0" />
                <a
                  href="tel:+3228806650"
                  data-testid="footer-phone"
                  className="text-slate-400 hover:text-white transition-colors text-sm"
                >
                  +32 2 880 66 50
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-[#10B981] shrink-0" />
                <a
                  href="mailto:info@nettis.be"
                  data-testid="footer-email"
                  className="text-slate-400 hover:text-white transition-colors text-sm"
                >
                  info@nettis.be
                </a>
              </li>
            </ul>
            <div className="mt-6 pt-6 border-t border-slate-800">
              <p className="text-slate-500 text-xs">TVA: BE0788.309.595</p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © {currentYear} Nettis Facility. {t('droitsReserves')}.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/politique-confidentialite" className="text-slate-500 hover:text-white text-sm transition-colors">
              {t('politiqueConfidentialite')}
            </Link>
            <Link to="/mentions-legales" className="text-slate-500 hover:text-white text-sm transition-colors">
              {t('mentionsLegales')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
